import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, FolderOpen, Store, User, ShoppingCart, Package, MessageSquare, Tag, LayoutDashboard, Search } from 'lucide-react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { cartItemCount, useCartStore } from '@store/cartStore';
import { goToSection } from '@/utils/scrollToSection';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  match: (path: string) => boolean;
  badge?: number;
  onClick?: () => void;
};

interface BottomNavProps {
  mode?: 'marketing' | 'app';
}

export function BottomNav({ mode = 'app' }: BottomNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { items, toggleDrawer } = useCartStore();
  const count = cartItemCount(items);

  const panelPath =
    user?.role === 'admin'
      ? '/admin'
      : user?.role === 'wholesaler'
        ? '/wholesaler'
        : '/restaurant';

  const panelTab = (tab: string) => `${panelPath}?tab=${tab}`;

  let itemsNav: NavItem[];

  if (mode === 'marketing' && !isAuthenticated) {
    itemsNav = [
      { to: '/', label: 'Ana Sayfa', icon: Home, match: (path) => path === '/' },
      {
        to: '/#categories',
        label: 'Kategoriler',
        icon: FolderOpen,
        match: () => false,
        onClick: () => goToSection(navigate, 'categories'),
      },
      {
        to: '/#wholesalers',
        label: 'Toptancılar',
        icon: Store,
        match: () => false,
        onClick: () => goToSection(navigate, 'wholesalers'),
      },
      { to: '/login', label: 'Giriş', icon: User, match: (path) => path.startsWith('/login') },
    ];
  } else if (user?.role === 'restaurant') {
    itemsNav = [
      { to: panelTab('browse'), label: 'Keşfet', icon: Store, match: (path) => path.startsWith('/restaurant') },
      { to: panelTab('orders'), label: 'Siparişler', icon: Package, match: (path) => path.includes('tab=orders') },
      {
        to: '#cart',
        label: 'Sepet',
        icon: ShoppingCart,
        badge: count,
        match: () => false,
        onClick: () => toggleDrawer(),
      },
      { to: panelTab('reminders'), label: 'Hatırlatıcı', icon: LayoutDashboard, match: (path) => path.includes('tab=reminders') },
    ];
  } else if (user?.role === 'wholesaler') {
    itemsNav = [
      { to: panelTab('orders'), label: 'Siparişler', icon: Package, match: (path) => path.includes('tab=orders') },
      { to: panelTab('messages'), label: 'Mesajlar', icon: MessageSquare, match: (path) => path.includes('tab=messages') },
      { to: panelTab('products'), label: 'Ürünler', icon: Tag, match: (path) => path.includes('tab=products') },
      { to: panelPath, label: 'Panel', icon: LayoutDashboard, match: (path) => path.startsWith('/wholesaler') },
    ];
  } else if (user?.role === 'admin') {
    itemsNav = [
      { to: '/', label: 'Ana Sayfa', icon: Home, match: (path) => path === '/' },
      { to: panelPath, label: 'Panel', icon: LayoutDashboard, match: (path) => path.startsWith('/admin') },
      { to: '/search', label: 'Ara', icon: Search, match: (path) => path.startsWith('/search') },
    ];
  } else {
    itemsNav = [
      { to: '/', label: 'Ana Sayfa', icon: Home, match: (path) => path === '/' },
      { to: '/login', label: 'Giriş', icon: User, match: (path) => path.startsWith('/login') },
    ];
  }

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-header backdrop-blur-xl md:hidden"
      aria-label="Mobil navigasyon"
    >
      <div className="mx-auto flex max-w-lg items-stretch justify-around">
        {itemsNav.map((item) => {
          const active = item.match(location.pathname + location.search);
          const Icon = item.icon;
          const className = clsx(
            'relative flex min-w-0 flex-1 flex-col items-center gap-0.5 px-2 py-2.5 text-[11px] font-medium transition',
            active ? 'text-brand-600' : 'text-slate-500',
          );

          const content = (
            <>
              <span className={clsx('relative', active && 'after:absolute after:-top-2 after:left-1/2 after:h-0.5 after:w-5 after:-translate-x-1/2 after:rounded-full after:bg-brand-500')}>
                <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} />
                {item.badge ? (
                  <span className="absolute -right-2 -top-1 rounded-full bg-brand-500 px-1 text-[10px] font-bold text-white">
                    {item.badge}
                  </span>
                ) : null}
              </span>
              {item.label}
            </>
          );

          if (item.onClick) {
            return (
              <button key={item.label} type="button" className={className} onClick={item.onClick}>
                {content}
              </button>
            );
          }

          return (
            <Link key={item.to} to={item.to} className={className}>
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
